/**
 * @module constants/SourceTypes
 */
import invert from 'lodash/invert';

import { type Inverted } from 'src/types';

export const ThumbprintSeedId = 'TT:0';
export const HybridStationSeedId = 'HS:';
export const GenreSeedId = 'G';

export const PandoraTypes = Object.freeze({
  ALBUM: 'AL',
  ARTIST: 'AR',
  ARTIST_ALL_SONGS: 'AT',
  ARTIST_TOP_SONGS: 'AP',
  AUTOPLAY: 'AU',
  BROWSE_DIRECTORY: 'BR',
  COLLECTED_ALBUM_TRACKS: 'CA',
  COLLECTED_TRACKS: 'CT',
  COMPOSER: 'CO',
  CURATOR: 'CU',
  DOWNLOADED_TRACKS: 'DT',
  GENRE: 'GE',
  PLAYLIST: 'PL',
  PODCAST: 'PC',
  PODCAST_AUTOPLAY: 'PA',
  EPISODE: 'PE',
  SONG: 'TR',
  STATION: 'SF',
  STATION_SAMPLE_TRACKS: 'SS',
  STATION_THUMB_UP: 'TU',
  USER_STATION: 'ST', // User created stations
  HYBRID_STATION: 'HS',
  VOICE_TRACK: 'AM',
} as const);

// TODO: Unify SourceType and PandoraType, JIRA: WEBCLIENT-6409
export const SourceTypes = Object.freeze({
  ALBUM: 'ALBUM',
  ARTIST: 'ARTIST',
  AUTOPLAY: 'AUTOPLAY',
  BROWSE_DIRECTORY: 'BROWSE_DIRECTORY',
  COMPOSER: 'COMPOSER',
  CURATOR: 'CURATOR',
  GENRE: 'GENRE',
  PLAYLIST: 'PLAYLIST',
  PODCAST: 'PODCAST',
  PODCAST_AUTOPLAY: 'PODCAST_AUTOPLAY',
  EPISODE: 'EPISODE',
  SONG: 'SONG',
  STATION: 'STATION',
  USER_STATION: 'STATION',
  VOICE_TRACK: 'SONG',
  // PSEUDO SOURCES
  ARTIST_TOP_SONGS: 'ARTIST_TOP_SONGS',
  ARTIST_ALL_SONGS: 'ARTIST_ALL_SONGS',
  COLLECTED_TRACKS: 'COLLECTED_TRACKS',
  COLLECTED_ALBUM_TRACKS: 'COLLECTED_ALBUM_TRACKS',
  STATION_THUMB_UP: 'STATION_THUMB_UP',
} as const);

export const PodcastSourceTypes = [
  SourceTypes.PODCAST,
  SourceTypes.EPISODE,
  SourceTypes.PODCAST_AUTOPLAY,
] as const;

export const RecentlyPlayedSourceTypes = [
  SourceTypes.ALBUM,
  SourceTypes.SONG,
  SourceTypes.PLAYLIST,
  SourceTypes.STATION,
  SourceTypes.ARTIST_TOP_SONGS,
  SourceTypes.PODCAST,
] as const;

export const PseudoSourceTypes = [
  SourceTypes.ARTIST_TOP_SONGS,
  SourceTypes.ARTIST_ALL_SONGS,
  SourceTypes.COLLECTED_TRACKS,
  SourceTypes.COLLECTED_ALBUM_TRACKS,
  SourceTypes.STATION_THUMB_UP,
] as const;

export const PseudoObfuscations = Object.freeze({
  [PandoraTypes.ALBUM]: '16716',
  [PandoraTypes.ARTIST]: '16722',
  [PandoraTypes.COMPOSER]: '1723',
} as const);

export const PseudoObfuscationTypes = invert(PseudoObfuscations) as Inverted<
  typeof PseudoObfuscations
>;

export const ArtistPseudoSourceTypes = [SourceTypes.ARTIST_TOP_SONGS, SourceTypes.ARTIST_ALL_SONGS];

export const PlaylistSupportedTypes = Object.freeze([PandoraTypes.SONG, PandoraTypes.VOICE_TRACK]);

export const PandoraTypeSources = invert(PandoraTypes);

export const SearchTypes = Object.freeze({
  ADVERTISER: 'advertiser',
  ALL: 'all',
  ARTIST: 'artist',
  ARTIST_STATION: 'artistStation',
  COMPOSER: 'composer',
  GENRE: 'genre',
  SONG: 'song',
  THUMBPRINT: 'thumbprint',
  TRACK: 'track',
} as const);

export const SearchT3Types = Object.freeze({
  ADVERTISER: 'advertiser',
  ALBUMS: 'albums',
  ALL: 'all',
  ARTISTS: 'artists',
  ARTIST_STATION: 'artistStation',
  GENRES: 'genres',
  PLAYLISTS: 'playlists',
  PODCASTS: 'podcasts',
  SONGS: 'songs',
  STATIONS: 'stations',
  THUMBPRINT: 'thumbprint',
  TRACKS: 'tracks',
} as const);

export const StationTypes = [
  'SF:AR', // Artist Station
  'SF:CO', // Composer Station
  'SF:TR', // Track Station
  'SF:GE', // Genre Station
  'SF:HS', // Hybrid Station
  'SF:TT', // Thumbprint Radio Station
  'ST', // User-created Station
] as const;

export const DeepLinkContentTypes = Object.freeze({
  AL: 'Album',
  AR: 'Artist',
  CO: 'Artist',
  CU: 'Curator',
  PC: 'Podcast',
  PE: 'Episode',
  PL: 'Playlist',
  SF: 'Station',
  ST: 'Station',
  TR: 'Track',
} as const);

export default SourceTypes;
